import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { LucyAvatar } from "./Lucy";
import { fmtL } from "./BpKit";
import { dshort } from "./WeeklyPo";

const H = ({ children }) => <h4 className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{children}</h4>;
const when = (s) => new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });

const PrePos = ({ rows }) => (
  <div data-testid="meeting-brief-prepos">
    <H>Open Pre-POs</H>
    {!rows.length ? <p className="text-sm text-slate-500">No open Pre-POs for this client.</p> : (
      <ul className="space-y-1">
        {rows.map((o) => (
          <li key={o.uid} className="flex flex-wrap items-center gap-2 text-sm">
            <Link to={`/orders/uid/${o.uid}`} className="num font-medium text-primary hover:underline">{o.uid}</Link>
            <span className="num">{fmtL(o.value)}</span>
            {o.planned_po_date && <span className={`text-xs ${o.late ? "text-rose-700" : "text-slate-500"}`}>PO planned {dshort(o.planned_po_date)}{o.late ? " · passed" : ""}</span>}
            {o.on_hold && <span className="text-[10px] px-1 border border-amber-300 text-amber-700 rounded-sm">On hold</span>}
            {o.owner && <span className="text-xs text-slate-400">{o.owner}</span>}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Plan = ({ rows }) => (
  <div data-testid="meeting-brief-plan">
    <H>Plan gap</H>
    {!rows.length ? <p className="text-sm text-slate-500">This client isn't in the sales plan.</p> : rows.map((r) => (
      <div key={r.branch} className="text-sm space-y-0.5 mb-2">
        <div className="text-xs text-slate-500">{r.branch} · Class {r.class}</div>
        <div>This month: <b className="num">{r.this_month_booked == null ? "—" : fmtL(r.this_month_booked)}</b> of <span className="num">{fmtL(r.this_month_plan)}</span>
          {r.this_month_gap ? <span className="text-rose-700"> · gap {fmtL(r.this_month_gap)}</span> : null}</div>
        <div>FY: <b className="num">{r.fy_booked == null ? "—" : fmtL(r.fy_booked)}</b> of <span className="num">{fmtL(r.fy_plan)}</span>
          {r.fy_gap ? <span className="text-rose-700"> · gap {fmtL(r.fy_gap)}</span> : null}{r.expected_po_this_week ? <span className="text-slate-500"> · expected this week {fmtL(r.expected_po_this_week)}</span> : null}</div>
      </div>
    ))}
  </div>
);

const Notes = ({ rows }) => (
  <div data-testid="meeting-brief-notes">
    <H>Last notes</H>
    {!rows.length ? <p className="text-sm text-slate-500">Nothing logged for this client yet.</p> : (
      <ul className="space-y-2">
        {rows.map((n) => (
          <li key={`${n.at}-${n.text.slice(0, 12)}`} className="text-sm">
            <div className="text-xs text-slate-500">{when(n.at)} · {n.type.replace("_", " ")} · {n.by_name}{n.uid ? ` · ${n.uid}` : ""}</div>
            <div className="whitespace-pre-wrap">{n.text}</div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const MeetingBrief = ({ m, open, onOpenChange }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    setD(null);
    document.body.dataset.lucy = "open";
    toast.dismiss("lead-nudge");
    api.get(`/lucy/meeting-brief/${m.meeting_id}`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
    return () => { delete document.body.dataset.lucy; };
  }, [open, m.meeting_id, onOpenChange]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="meeting-brief-panel">
        <SheetHeader className="text-left space-y-0.5 pr-6">
          <SheetTitle className="text-base">Meeting brief · {m.client}</SheetTitle>
          <SheetDescription className="text-xs">{m.planned_date ? `Planned for ${dshort(m.planned_date)}` : "No date set"}{d?.facts.account && d.facts.account !== m.client ? ` · account ${d.facts.account}` : ""}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-5">
          <div className="border border-violet-200 bg-violet-50/40 rounded-sm p-3" data-testid="meeting-brief-lucy">
            <div className="flex items-center gap-2 mb-2"><LucyAvatar size="h-7 w-7 text-xs" /><span className="text-sm font-semibold">Lucy's brief</span></div>
            {!d ? <p className="text-xs text-slate-500 animate-pulse" data-testid="meeting-brief-loading">Lucy is reading up on {m.client}…</p>
              : d.brief ? <ul className="space-y-1.5" data-testid="meeting-brief-lines">{d.brief.map((x, i) => <li key={x} className="text-sm leading-relaxed flex gap-2" data-testid={`meeting-brief-line-${i}`}><span className="text-violet-600 font-bold">·</span>{x}</li>)}</ul>
                : <p className="text-sm text-amber-700" data-testid="meeting-brief-ai-error">Lucy couldn't write the brief just now. The facts below are up to date.</p>}
          </div>
          {d && <><PrePos rows={d.facts.pre_pos} /><Plan rows={d.facts.plan} /><Notes rows={d.facts.notes} /></>}
        </div>
      </SheetContent>
    </Sheet>
  );
};
